import {Range, Map} from 'immutable';
import type {List} from 'immutable';

/**
 * `this` type for the passage-correction extension methods. These methods are
 * spread onto the JSON:API question model (see `baseQuestion.extensions.js`),
 * so `this` exposes the underlying Immutable-backed model accessors (`get`,
 * `cache`, `getValidResponse`) alongside the sibling extension methods.
 */
interface PassageCorrectionQuestionThis {
  get(key: string): any;
  get<T>(key: string, notSetValue: T): T;
  cache<T>(key: string, func: () => T): T;
  getValidResponse(): Map<string, any>;
  getRegionIndices(): Map<string, Map<string, number>>;
  getEnumeratedRegionIndices(): Map<number, boolean>;
  getRegionKeyForOptionIndex(optionIndex: number): string;
}

export const passageCorrectionQuestionExtensions = {
  getUncorrectedText(this: PassageCorrectionQuestionThis): string {
    return this.get('uncorrected_text');
  },

  getRegionIndices(this: PassageCorrectionQuestionThis): Map<string, Map<string, number>> {
    return this.get('region_indices', Map());
  },

  getEnumeratedRegionIndices(this: PassageCorrectionQuestionThis): Map<number, boolean> {
    const func = () =>
      this.getRegionIndices().reduce(
        (acc: Map<number, boolean> = Map(), region?: Map<string, number>) => {
          const indicesInRegion = Range(
            region!.get('start_index'),
            region!.get('end_index') + 1
          ).reduce((innerAcc: Map<number, boolean> = Map(), val?: number) => {
            return innerAcc.set(val as number, true);
          }, Map<number, boolean>());
          return acc.merge(indicesInRegion);
        },
        Map<number, boolean>()
      );

    return this.cache('getEnumeratedRegionIndices', func);
  },

  isOptionIndexInteractive(this: PassageCorrectionQuestionThis, optionIndex: number): boolean {
    return this.getEnumeratedRegionIndices().get(parseInt(String(optionIndex), 10)) === true;
  },

  getRegionForOptionIndex(
    this: PassageCorrectionQuestionThis,
    optionIndex: number
  ): Map<string, any> {
    const matchedRegion = this.getRegionKeyForOptionIndex(optionIndex);
    return this.getValidResponse().get(matchedRegion);
  },

  getRegionKeyForOptionIndex(
    this: PassageCorrectionQuestionThis,
    optionIndex: number
  ): string {
    const func = () =>
      this.getRegionIndices().findKey((region?: Map<string, number>) => {
        return (
          [region!.get('start_index'), optionIndex, region!.get('end_index')].sort(
            (a, b) => a - b
          )[1] === optionIndex
        );
      });
    return this.cache(`getRegionKeyForOptionIndex/${optionIndex}`, func);
  },

  getSolutionsForRegionKey(
    this: PassageCorrectionQuestionThis,
    regionKey: string
  ): List<string> {
    return this.getValidResponse().getIn([regionKey, 'solutions']);
  },

  isRegionDistractor(
    this: PassageCorrectionQuestionThis & {
      getSolutionsForRegionKey(regionKey: string): List<string>;
    },
    regionKey: string
  ): boolean {
    return this.getSolutionsForRegionKey(regionKey) === null;
  },

  doesRegionContainOptionIndex(region: Map<string, any>, optionIndex: number): boolean {
    const matchedStartIndex = region.get('start_index');
    const matchedEndIndex = region.get('end_index');
    return matchedStartIndex <= optionIndex && optionIndex <= matchedEndIndex;
  }
};
